import React from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, ScanSearch, FileCode2, Rocket, PlusCircle, ShieldCheck, KeyRound, Repeat2 } from "lucide-react";

const STEPS = [
  {
    icon: GitBranch,
    title: "Submit Repository",
    description:
      "Provide your Git repository URL, target branch, and deployment target (GCP or Ubuntu VM). Private repositories are supported via SSH deploy keys.",
  },
  {
    icon: ScanSearch,
    title: "AI Analysis",
    description:
      "GoLive clones the repository and uses an LLM to analyse the codebase — detecting language, framework, dependencies, and infrastructure requirements.",
  },
  {
    icon: FileCode2,
    title: "Review & Generate Script",
    description:
      "A deployment proposal is generated for you to review and approve. Once approved, GoLive generates a ready-to-run deployment script tailored to your stack.",
  },
  {
    icon: Rocket,
    title: "Download & Deploy",
    description:
      "Download the generated deployment script and run it on your target environment at your own pace. The script is fully self-contained and ready to execute.",
  },
];

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: "Human-in-the-loop",
    description: "Every deployment proposal requires a human review and approval before a script is generated — nothing is produced without sign-off.",
  },
  {
    icon: KeyRound,
    title: "Secure by default",
    description: "Private repositories use ephemeral Ed25519 SSH deploy keys scoped to a single repo. Keys are encrypted at rest.",
  },
  {
    icon: Repeat2,
    title: "Full audit trail",
    description: "Every run captures who submitted, who approved, which model was used, and what was deployed — ready for compliance reviews.",
  },
];

export default function GoLiveHomePage() {
  const navigate = useNavigate();

  return (
    <div className="gl-page max-w-4xl">
      {/* Hero */}
      <div className="gl-hero mb-10">
        <div className="gl-hero-glow" aria-hidden="true" />
        <h1 className="gl-hero-title">Orion GoLive</h1>
        <p className="gl-hero-body max-w-2xl">
          GoLive is an AI-powered deployment pipeline that takes your Git repository from source code
          and automatically analyses your stack, then generates ready-to-run deployment scripts
          tailored to your target infrastructure.
        </p>
        <button
          type="button"
          onClick={() => navigate("/golive/new")}
          className="gl-btn primary mt-6"
        >
          <PlusCircle size={16} />
          New Deployment
        </button>
      </div>

      {/* Pipeline steps */}
      <div className="mb-10">
        <h2 className="gl-section-label mb-4">How it works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {STEPS.map(({ icon: Icon, title, description }, i) => (
            <div key={title} className="gl-card flex gap-4">
              <div className="shrink-0 flex flex-col items-center gap-1">
                <div className="gl-step-ico">
                  <Icon size={16} />
                </div>
                <span className="gl-step-num">0{i + 1}</span>
              </div>
              <div>
                <p className="gl-step-title">{title}</p>
                <p className="gl-step-desc">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Highlights */}
      <div>
        <h2 className="gl-section-label mb-4">Key principles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="gl-card">
              <div className="gl-step-ico gold mb-3">
                <Icon size={16} />
              </div>
              <p className="gl-step-title">{title}</p>
              <p className="gl-step-desc">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
